import React, { useState, useMemo } from 'react';
import { formatNameForDisplay } from '../../utils/nameUtils';
import { getGenderTextColor, getGenderAvatarBg } from '../../utils/genderColors';
import { StatusBadge, MobileSearchField } from '../common';

/**
 * Read-only list of players at the session (active, signed up, paused).
 * Shown above Log Game for non-host/admins so they can see who's at the session before selecting from the dropdown.
 */
const SessionPlayersList = ({
  players = [],
  sessionPlayerStatus = {},
  sessionPlayerExitReasons = {},
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const atSessionStatuses = ['active', 'signed_up', 'paused'];
  const playersAtSession = useMemo(() => {
    let list = players.filter((p) => {
      const status = sessionPlayerStatus[p.id];
      return status !== undefined && atSessionStatuses.includes(status);
    });
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      const nameStr = (p) => (p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()).toLowerCase();
      list = list.filter((p) => nameStr(p).includes(term));
    }
    list.sort((a, b) => {
      const aName = (a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim()).toLowerCase();
      const bName = (b.name || `${b.first_name || ''} ${b.last_name || ''}`.trim()).toLowerCase();
      return aName.localeCompare(bName);
    });
    return list;
  }, [players, sessionPlayerStatus, searchTerm]);

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <StatusBadge variant="success" size="small">Playing</StatusBadge>;
    }
    if (status === 'signed_up') {
      return <StatusBadge variant="primary" size="small">Signed up</StatusBadge>;
    }
    if (status === 'paused') {
      return <StatusBadge variant="warning" size="small">Paused</StatusBadge>;
    }
    return null;
  };

  return (
    <div className="bg-surface rounded-lg border border-border-medium shadow-sm p-4 mb-6">
      <h3 className="text-base font-semibold text-text-primary mb-1">Players at this session</h3>
      <p className="text-sm text-text-secondary mb-3">
        Use the list below to see who&apos;s here, then select players in the Log a Game form.
      </p>
      <div className="mb-3">
        <MobileSearchField
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-xs px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary focus:ring-2 focus:ring-primary focus:border-primary"
        />
      </div>
      {playersAtSession.length === 0 ? (
        <p className="text-sm text-text-muted py-2">No players at the session yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {playersAtSession.map((player) => {
            const status = sessionPlayerStatus[player.id];
            return (
              <div
                key={player.id}
                className="flex items-center gap-2 px-3 py-2 bg-surface-subtle rounded-lg border border-border-medium"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 ${getGenderAvatarBg(player.gender)}`}
                >
                  {(player.name || player.first_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-sm font-medium truncate ${getGenderTextColor(player.gender)}`}>
                    {formatNameForDisplay(player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim(), { maxLength: 24, useInitials: false }).display}
                  </span>
                  {getStatusBadge(status)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionPlayersList;
